import { memo, useMemo, useState } from 'react';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn, formatPrice } from '@/lib/utils';
import { embedLayout } from '@/lib/embed-layout';
import type { UIProduct } from '@/lib/product-transformer';
import {
  findTemplateByPosition,
  getDefaultTemplate,
} from '@/lib/product-transformer';
import type { PerPositionAdaptation } from '@/hooks/useAdaptedPreviews';
import { ProductMockupWithTemplate } from './ProductCard';
import { resolveDisplayColor } from './displayColor';
import { useSwatchCapacity, type SwatchRowGeometry } from './swatchCapacity';

/**
 * A catalogue card, after the guided-entry design's "Katalog" screen.
 *
 * Lean on purpose: the seller is browsing, not pricing. Photo, badges, fit,
 * name, maker, colours, Print Area chips, purchase price and one switch to
 * pick the product. Prices and profit belong to the products step.
 *
 * The photo is the product's catalogue image (`thumbnailImageUrl` from the
 * sync — the same studio shot the admin catalogue and Direct Sale show). It is
 * one photo per product, so the colour dots do not change it. With "Design
 * anzeigen" on, the card shows the packshot with the design rendered on it
 * instead — the same renderer the products step uses. Without a catalogue
 * image the packshot stands in, marked as such.
 */

export interface CatalogCardProps {
  product: UIProduct;
  /** Why this product does not carry the design; null when it fits. */
  mismatchLabel?: string | null;
  /** Effective design width per Position on this product. */
  areaChips?: string[];
  /** Images fall below the method's minimum dpi on this product. */
  dpiWarning?: string | null;
  /** "Design anzeigen": render the design on the packshot instead of the photo. */
  showDesign: boolean;
  frontPreviewUrl?: string | null;
  positionAdaptations?: Map<string, PerPositionAdaptation> | null;
  onToggle: (productId: string, isActive: boolean) => void;
}

// Swatch geometry for the collapsed row, which is sized to the card width.
const DOT_GEOMETRY: SwatchRowGeometry = {
  swatchSize: 13, // px — h/w-[13px]
  gap: 4, // px — gap-1
  toggleWidth: 44 + 4, // px — the "+N" expand button and the gap before it
};
const FALLBACK_DOTS = 5; // until the row has been measured

export const CatalogCard = memo(function CatalogCard({
  product,
  mismatchLabel = null,
  areaChips,
  dpiWarning = null,
  showDesign,
  frontPreviewUrl = null,
  positionAdaptations = null,
  onToggle,
}: CatalogCardProps) {
  const { t } = useTranslation();
  const color = useMemo(
    () =>
      resolveDisplayColor({
        colors: product.colors,
        selectedColors: product.selectedColors,
        focusedColorId: null,
        isFocused: false,
      }),
    [product.colors, product.selectedColors]
  );
  const shownColor = product.colors.find((c) => c.id === color.id);
  const photo = product.thumbnailUrl ?? null;

  const frontTemplate = useMemo(
    () =>
      findTemplateByPosition(product.mockupTemplates, 'front') ||
      getDefaultTemplate(product.mockupTemplates) ||
      null,
    [product.mockupTemplates]
  );
  const frontAdaptation = positionAdaptations?.get('front') ?? null;
  // Hovering the photo reveals the design on this product. The mockup layer
  // mounts on the first hover so a grid of 125 cards does not render 125
  // mockups up front; afterwards it stays mounted and just fades.
  const [revealed, setRevealed] = useState(false);
  const hasDesign =
    frontTemplate !== null &&
    Boolean(frontPreviewUrl || frontAdaptation?.cropUrl);

  const renderDesign = showDesign && frontTemplate !== null;
  const [colorsExpanded, setColorsExpanded] = useState(false);
  const [dotsRowEl, setDotsRowEl] = useState<HTMLDivElement | null>(null);
  const colorCount = product.colors.length;
  const dotCapacity =
    useSwatchCapacity(dotsRowEl, colorCount, DOT_GEOMETRY) ?? FALLBACK_DOTS;
  const dots = colorsExpanded
    ? product.colors
    : product.colors.slice(0, dotCapacity);
  const moreColors = colorCount - dotCapacity;
  const hasMoreColors = moreColors > 0;

  const toggle = () => onToggle(product.id, !product.isActive);
  // Compact embedding: the switch becomes a check mark on the photo (the
  // dc-pick role) and the widths a badge on it — one switch and a chip row
  // per card dominated the grid.
  const compact = embedLayout().tileStyle === 'media';
  // Per product: the motif's width follows each product's own print area.
  const showAreaChips =
    !mismatchLabel && areaChips !== undefined && areaChips.length > 0;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={product.isActive}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      }}
      data-selected={product.isActive || undefined}
      className={cn(
        'dc-tile flex cursor-pointer flex-col overflow-hidden rounded-card bg-[var(--paper-2)] p-2 transition-[box-shadow,opacity] duration-[120ms]',
        product.isActive
          ? 'shadow-[inset_0_0_0_2px_var(--cobalt-500)]'
          : 'shadow-[inset_0_0_0_1px_var(--line)] hover:shadow-[inset_0_0_0_1px_var(--primary)]',
        mismatchLabel && 'opacity-60 hover:opacity-100'
      )}
    >
      <div
        className={cn(
          'dc-well group/tile relative aspect-[4/5] overflow-hidden rounded-control',
          // Photos and rendered mockups sit on a white base, so the tile is
          // the photo well; only the packshot fallback keeps the paper.
          renderDesign || photo ? 'bg-photo' : 'bg-[var(--paper)]'
        )}
        onPointerEnter={() => {
          if (hasDesign) setRevealed(true);
        }}
      >
        {renderDesign ? (
          <ProductMockupWithTemplate
            product={product}
            template={frontTemplate}
            backgroundColor={color.hex}
            isHeathered={shownColor?.isHeathered ?? false}
            designDataUrl={frontPreviewUrl}
            adaptedPlacement={frontAdaptation?.placement ?? null}
            designCropUrl={frontAdaptation?.cropUrl ?? null}
            frameBackground="var(--photo)"
          />
        ) : photo ? (
          <>
            <img
              src={photo}
              alt=""
              draggable={false}
              loading="lazy"
              className={cn(
                // Phase one: the photo fades into the white tile, quickly and
                // without delay on enter; on leave it returns after the mockup
                // has gone, so the two never overlap half-transparent.
                'h-full w-full object-cover transition-opacity duration-[120ms] ease-[var(--ease-out)] delay-[140ms] motion-reduce:transition-none',
                hasDesign &&
                  revealed &&
                  'group-hover/tile:opacity-0 group-hover/tile:delay-0'
              )}
            />
            {hasDesign && revealed && (
              <div
                data-testid="hover-design"
                // Phase two: a solid white layer with the mockup settles in
                // with a slight zoom once the photo is gone; on leave it drops
                // out first so the photo can return.
                className="absolute inset-0 scale-[0.96] bg-photo opacity-0 transition-[opacity,transform] duration-[200ms] ease-[var(--ease-out)] delay-0 group-hover/tile:scale-100 group-hover/tile:opacity-100 group-hover/tile:delay-[140ms] motion-reduce:transition-none motion-reduce:scale-100"
              >
                <ProductMockupWithTemplate
                  product={product}
                  template={frontTemplate}
                  backgroundColor={color.hex}
                  isHeathered={shownColor?.isHeathered ?? false}
                  designDataUrl={frontPreviewUrl}
                  adaptedPlacement={frontAdaptation?.placement ?? null}
                  designCropUrl={frontAdaptation?.cropUrl ?? null}
                  frameBackground="var(--photo)"
                />
              </div>
            )}
          </>
        ) : (
          <ProductMockupWithTemplate
            product={product}
            template={frontTemplate}
            backgroundColor={color.hex}
            isHeathered={shownColor?.isHeathered ?? false}
          />
        )}

        {product.isPremium && (
          <span
            data-tone="success"
            className="dc-media-badge dc-type-chip absolute right-2 top-2 rounded-chip bg-[var(--paper)] px-2 py-0.5 text-[var(--success)] ring-1 ring-inset ring-[var(--line)]"
          >
            {t('creator.catalog.organic')}
          </span>
        )}
        {!renderDesign && !photo && (
          <span className="dc-media-badge dc-type-chip absolute left-2 top-2 rounded-chip bg-[var(--paper)] px-2 py-0.5 text-[var(--mute)] ring-1 ring-inset ring-[var(--line)]">
            {t('creator.catalog.photoPending')}
          </span>
        )}
        {mismatchLabel && (
          <span className="dc-media-badge dc-type-chip absolute bottom-2 left-2 right-2 truncate rounded-chip bg-[var(--paper)] px-2 py-0.5 text-[var(--danger)] ring-1 ring-inset ring-[var(--line)]">
            {mismatchLabel}
          </span>
        )}
        {/* Admin embedding: the widths ride on the photo as one small badge
            ("Front 28 cm / Back 24 cm") instead of a row of chips below. */}
        {compact && showAreaChips && (
          <span className="dc-media-badge dc-type-data absolute bottom-2 right-2 max-w-[calc(100%-16px)] truncate">
            {areaChips.join(' / ')}
          </span>
        )}
        {compact && !mismatchLabel && dpiWarning && (
          <span
            role="status"
            className="dc-media-badge dc-type-chip absolute bottom-2 left-2 max-w-[60%] truncate rounded-chip bg-[var(--paper)] px-2 py-0.5 text-[var(--warning)] ring-1 ring-inset ring-[var(--line)]"
          >
            {dpiWarning}
          </span>
        )}
      </div>

      <div className="dc-tile-body flex flex-col gap-1.5 px-1.5 pb-1 pt-2.5">
        <div className="dc-type-card-title truncate text-[var(--ink)]">
          {product.name}
        </div>
        {product.manufacturerName && (
          <div className="truncate text-[11.5px] text-[var(--mute)]">
            {product.manufacturerName}
          </div>
        )}

        {dots.length > 0 && (
          <div
            ref={setDotsRowEl}
            className={cn(
              'flex gap-1',
              colorsExpanded ? 'flex-wrap items-start' : 'items-center'
            )}
          >
            {dots.map((c) => (
              <span
                key={c.id}
                title={c.name}
                className="inline-block h-[13px] w-[13px] rounded-full shadow-[inset_0_0_0_1px_rgba(13,13,13,0.18)]"
                style={{ background: c.hex }}
              />
            ))}
            {hasMoreColors && (
              <button
                type="button"
                aria-expanded={colorsExpanded}
                aria-label={t(
                  colorsExpanded
                    ? 'creator.productCard.collapseColors'
                    : 'creator.productCard.expandColors'
                )}
                // The card itself is a button: keep the toggle from picking
                // or unpicking the product.
                onClick={(e) => {
                  e.stopPropagation();
                  setColorsExpanded((v) => !v);
                }}
                onKeyDown={(e) => e.stopPropagation()}
                className="dc-type-data inline-flex items-center gap-0.5 text-[var(--mute)] hover:text-[var(--ink)]"
              >
                {!colorsExpanded && <span>+{moreColors}</span>}
                {colorsExpanded ? (
                  <ChevronUp className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
              </button>
            )}
          </div>
        )}

        {!compact && (showAreaChips || (!mismatchLabel && dpiWarning)) && (
          <div className="flex flex-wrap gap-1">
            {showAreaChips &&
              areaChips.map((chip) => (
                <span
                  key={chip}
                  className="dc-chip dc-type-chip catalog-area-chip rounded-chip bg-[var(--paper)] px-2 py-0.5 text-[var(--ink-2)] ring-1 ring-inset ring-[var(--line)]"
                >
                  {chip}
                </span>
              ))}
            {!mismatchLabel && dpiWarning && (
              <span
                role="status"
                className="dc-chip dc-type-chip catalog-area-chip rounded-chip bg-[var(--paper)] px-2 py-0.5 text-[var(--warning)] ring-1 ring-inset ring-[var(--line)]"
              >
                {dpiWarning}
              </span>
            )}
          </div>
        )}

        <div className="mt-1 flex items-center justify-between gap-2">
          <span className="flex items-baseline gap-1">
            <span className="dc-type-price text-[var(--ink)]">
              {product.startingCost === null
                ? '—'
                : t('creator.catalog.startingCostFrom', {
                    amount: formatPrice(product.startingCost),
                  })}
            </span>
            <span className="dc-type-label text-[var(--mute)]">
              {t('creator.catalog.costShort')}
            </span>
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={product.isActive}
            aria-label={
              product.isActive
                ? t('creator.catalog.deselectProduct', { name: product.name })
                : t('creator.catalog.selectProduct', { name: product.name })
            }
            onClick={(e) => {
              e.stopPropagation();
              toggle();
            }}
            className={cn(
              'relative h-[22px] w-[38px] flex-none rounded-full transition-colors duration-[120ms]',
              'dc-pick',
              product.isActive ? 'bg-[var(--cobalt-500)]' : 'bg-[var(--line)]'
            )}
          >
            {compact ? (
              <Check aria-hidden="true" className="h-3.5 w-3.5" />
            ) : (
              <span
                className={cn(
                  'absolute top-[3px] h-4 w-4 rounded-full bg-paper transition-[left] duration-[120ms]',
                  product.isActive ? 'left-[19px]' : 'left-[3px]'
                )}
              />
            )}
          </button>
        </div>
      </div>
    </div>
  );
});
